// Optional effects start near visibility. Touch waits for settled scrolling;
// rendering itself remains continuous while visible.
import { afterFirstPaint } from "./after-paint";

export function observeVisible(
  element: HTMLElement,
  start: () => void,
  rootMargin = "0px"
) {
  let visible = false;
  let done = false;
  let suspended = false;
  let timer = 0;
  let cancelPaint: (() => void) | undefined;
  const delay = matchMedia("(pointer: coarse)").matches ? 220 : 0;
  const schedule = () => {
    clearTimeout(timer);
    cancelPaint?.();
    cancelPaint = undefined;
    if (!(done || suspended || document.hidden || !visible)) {
      timer = window.setTimeout(() => {
        cancelPaint = afterFirstPaint(() => {
          cleanup();
          start();
        });
      }, delay);
    }
  };
  const scroll = () => {
    if (delay) {
      schedule();
    }
  };
  const hide = () => {
    suspended = true;
    clearTimeout(timer);
    cancelPaint?.();
  };
  const show = () => {
    suspended = false;
    schedule();
  };
  const observer = new IntersectionObserver(
    ([entry]) => {
      visible = entry?.isIntersecting ?? false;
      schedule();
    },
    { rootMargin }
  );
  const cleanup = () => {
    done = true;
    clearTimeout(timer);
    cancelPaint?.();
    observer.disconnect();
    window.removeEventListener("scroll", scroll);
    window.removeEventListener("pagehide", hide);
    window.removeEventListener("pageshow", show);
    document.removeEventListener("visibilitychange", schedule);
  };
  observer.observe(element);
  window.addEventListener("scroll", scroll, { passive: true });
  window.addEventListener("pagehide", hide);
  window.addEventListener("pageshow", show);
  document.addEventListener("visibilitychange", schedule);
  return cleanup;
}
